import { useGuidedFilters } from '@Blocks/ProductList/GuidedFilters'
import { PageProductListBlockInjection } from '@Globals/Algolia/Types'

type GuidedFilter = ReturnType<typeof useGuidedFilters>[number]

type Injection = PageProductListBlockInjection | GuidedFilter

/**
 * Assign GI and GF items into slots.
 * @param bdis Block Data Injections
 * @param gfs Guided Filters
 * @returns Array of slots where index 0 = slot 1, index 1 = slot 2, etc.
 */
export function assignSlots(bdis: PageProductListBlockInjection[], gfs: GuidedFilter[]) {
  const result: (Injection | null)[] = []

  // Step 1: Place BDIs with explicit slots
  bdis.forEach((bdi) => {
    if (!bdi.slot || bdi.slot < 1) {
      return
    }

    const slotIndex = (bdi.slot as number) - 1 // convert to 0-based

    if (result[slotIndex]) {
      console.warn(`conflict assigning GI ${bdi.id} to slot ${bdi.slot}, already occupied by ${result[slotIndex]?.id}`)
      return
    }

    result[slotIndex] = bdi
  })

  let i = 0

  // Step 2: Place remaining BDIs in earliest available slots
  bdis.forEach((bdi) => {
    if (bdi.slot && bdi.slot >= 1) {
      return
    }

    while (result[i]) {
      i += 1
    }

    result[i] = bdi
  })

  // Step 3: Place GFs in earliest available slots
  gfs.forEach((gf) => {
    while (result[i]) {
      i += 1
    }

    result[i] = gf
  })

  return Array.from(result, item => item ?? null)
}

/**
 * Calculate how many slots can be used on a page,
 * taking into account placed injections.
 *
 * @param products Number of products on the page
 * @param assignedSlots Array of booleans (true if slot has an injection already)
 * @returns Number of available slots (0–4)
 */
export function calculateMaxSlots(products: number, assignedSlots: boolean[]): number {
  const baseRequirements = [14, 28, 42, 56] // per slot

  let available = 0

  for (let i = 0; i < 4; i += 1) {
    // Adjust requirement based on injections before this slot
    const filledBefore = assignedSlots.slice(0, i).filter(Boolean).length
    const adjustedRequirement = baseRequirements[i] - (filledBefore * 2)

    if (products < adjustedRequirement) {
      break
    }

    available = i + 1
  }

  return available
}

export const gridConfiguration = [
  {
    column: 'span 2 / -1',
    row: 4,
  },
  {
    column: '1 / span 2',
    row: 8,
  },
  {
    column: 'span 2 / -1',
    row: 12,
  },
  {
    column: '1 / span 2',
    row: 16,
  },
]
